import { action, computed, makeAutoObservable, observable } from "mobx";
import { createContext, useContext } from "react";
import { useLocalObservable } from "mobx-react-lite";

export interface Filter {
  column_id: string;
  value: string;
}

export class SearchStore {
  @observable _filters: Record<string, string[]> = {};
  @observable _globalSearchTerm: string | null = null;
  @observable _enabled = true;

  constructor() {
    makeAutoObservable(this);
  }

  @action
  updateFilter = (columnId: string, value: string) => {
    if (value === "") {
      delete this._filters[columnId];
    } else {
      if (!this._filters[columnId]) {
        this._filters[columnId] = [];
      }
      if (!this._filters[columnId].includes(value)) {
        this._filters[columnId] = [...this._filters[columnId], value];
      }
    }
  };

  @action
  removeFilter = (columnId: string, value: string) => {
    if (this._filters[columnId]) {
      this._filters[columnId] = this._filters[columnId].filter(v => v !== value);
      if (this._filters[columnId].length === 0) {
        delete this._filters[columnId];
      }
    }
  };

  @action
  setGlobalSearchTerm = (term: string | null) => {
    this._globalSearchTerm = term;
  };

  @action
  setEnabled = (enabled: boolean) => {
    this._enabled = enabled;
  };

  @action
  clearFilters = () => {
    this._filters = {};
  };

  @action
  reset = () => {
    this._filters = {};
    this._globalSearchTerm = null;
  };

  @computed
  get filters() {
    return this._filters;
  }

  @computed
  get globalSearchTerm() {
    return this._globalSearchTerm;
  }

  @computed
  get enabled() {
    return this._enabled;
  }
}

const SearchStoreContext = createContext<SearchStore | null>(null);

interface SearchProviderProps {
  children: React.ReactNode;
}

export const SearchProvider = ({ children }: SearchProviderProps) => {
  const store = useLocalObservable(() => new SearchStore());

  return <SearchStoreContext.Provider value={store}>{children}</SearchStoreContext.Provider>;
};

export const useSearch = () => {
  const context = useContext(SearchStoreContext);
  if (!context) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};

export const useSearchOptional = () => useContext(SearchStoreContext);
